import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTheme } from "@/contexts/ThemeContext";
import { apiService } from "@/services/api";
import { toast } from "sonner";
import { Logo } from "@/components/ui/Logo";
import { Mail, ArrowLeft, ArrowRight } from "lucide-react";

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPassword() {
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [userId, setUserId] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const email = watch("email");

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    try {
      const response = await apiService.forgotPassword(data.email);
      if (response.type === 0) {
        setUserId(response.data.refId);
        setEmailSent(true);
        toast.success("Password reset code sent to your email!");
      } else {
        toast.error(response.message || "Failed to send reset code");
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to send reset code. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    if (userId) {
      navigate("/verify-otp", { state: { userId, email, type: "forgot-password" } });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        {/* Back to Login */}
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Link>
        </div>

        {/* Forgot Password Card */}
        <Card className="border-2 shadow-lg">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto mb-4">
              <Logo size={48} showText={false} />
            </div>
            <CardTitle className="text-2xl font-bold">
              {emailSent ? "Check Your Email" : "Forgot Password"}
            </CardTitle>
            <CardDescription>
              {emailSent
                ? "We've sent a password reset code to your email address"
                : "Enter your email address to receive a password reset code"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!emailSent ? (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email address"
                      className="pl-10"
                      {...register("email")}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-sm text-destructive">{errors.email.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                  style={{ backgroundColor: currentTheme.colors.primary }}
                >
                  {isLoading ? "Sending..." : "Send Reset Code"}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <Mail className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    We've sent a 6-digit verification code to:
                  </p>
                  <p className="font-medium text-foreground">{email}</p>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={handleContinue}
                    className="w-full"
                    style={{ backgroundColor: currentTheme.colors.primary }}
                  >
                    Continue to Verification
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => {
                      setEmailSent(false);
                    }}
                    className="w-full"
                  >
                    Change Email Address
                  </Button>
                </div>

                <div className="text-center text-sm text-muted-foreground">
                  <p>Didn't receive the code?</p>
                  <Button
                    variant="link"
                    className="p-0 h-auto text-sm"
                    style={{ color: currentTheme.colors.primary }}
                    onClick={() => {
                      setEmailSent(false);
                    }}
                  >
                    Try again
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>Need help? Contact support</p>
        </div>
      </div>
    </div>
  );
}
